import { isEmpty, throttle } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router';
import Url from 'url-parse';

import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import StarsModal from '@swf/components/StarsModal/StarsModal';
import StarsSection from '@swf/components/StarsSection/StarsSection';
import type { PageState } from '@swf/core/models/page';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { StaticContext } from '@swf/core/types/StaticContext';
import { absoluteURL, canUseDOM, decodeQuery } from '@swf/core/utils';

import AlternateLinks from './components/AlternateLinks/AlternateLinks';
import CanonicalLink from './components/CanonicalLink/CanonicalLink';
import MetaDescription from './components/MetaDescription/MetaDescription';
import MetaNoIndex from './components/MetaNoIndex/MetaNoIndex';
import MetaTags from './components/MetaTags/MetaTags';
import MetaTitle from './components/MetaTitle/MetaTitle';
import NotFound from './components/NotFound/NotFound';

interface Props {
  page?: PageState;
  staticContext?: StaticContext;
}

const StarsPage: React.FC<Props> = ({ page, staticContext = {} }) => {
  const storePage = useSelector(select.page.get);
  const state = useSelector(select.getState);
  const { modal, mitid, ...query } = useSelector(select.location.getQuery);
  const brand = useSelector(select.site.getBrand);
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const dispatch = useDispatch<Dispatch>();
  const prevPathname = useRef(pathname);
  const { layout, meta } = page || storePage;
  const mitIdQuery = decodeQuery(mitid);
  const { href } = useSelector(select.location.get);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    throttle(() => {
      dispatch.localSettings.set({ redirectTo: pathname, registrationBrand: brand });
      if (prevPathname.current !== pathname) {
        prevPathname.current = pathname;
        dispatch.page.fetch({ href: window.location.href });
      }
    }, 200),
    [dispatch.page, pathname, brand]
  );

  useEffect(() => {
    if (!layout) dispatch.page.fetch({ href: window.location.href });

    return () => {
      if (isEmpty(storePage)) return;
      dispatch.page.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (modal) dispatch.modal.enqueue({ path: modal, locationState: mitIdQuery || query });
    else if (params.has('modal')) dispatch.modal.enqueue({ path: params.get('modal') as string });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, search]);

  if (modal || params.has('modal')) {
    const url = new Url(href, true);
    url.set('host', '');
    url.set('protocol', '');
    url.set('slashes', false);
    if (params.has('modal')) url.set('query', Object.fromEntries(params));
    delete url.query.mitid;
    delete url.query.modal;
    if (modal) dispatch.location.update(url);

    return <Redirect to={select.location.stripLocale(state, decodeURIComponent(url.toString()))} />;
  }

  staticContext.statusCode = layout ? 200 : 404;

  if (meta?.redirect && meta.redirect.statusCode && [301, 302].includes(meta.redirect.statusCode)) {
    staticContext.statusCode = meta.redirect.statusCode;
    const { location } = meta.redirect;

    if (absoluteURL(location)) {
      canUseDOM ? (window.location.href = location) : (staticContext.url = location);
      return null;
    }

    return <Redirect to={location} />;
  }

  return (
    <>
      <MetaTitle />
      <MetaDescription />
      <MetaNoIndex />
      <AlternateLinks />
      <CanonicalLink />
      <MetaTags />

      {layout ? (
        <>
          <StarsSection layout={layout} />
          <StarsModal />
        </>
      ) : (
        <>
          <CacheControl>no-store</CacheControl>
          <NotFound />
        </>
      )}
    </>
  );
};

export default StarsPage;
